import { POI_RULE } from "@eldrun/content";
import { BIOME, PLAN_SIDE, PLAN_SPACING, WORLD_HALF, WORLD_SIZE, HEIGHT_SIDE, type PlanData } from "@eldrun/worldgen";
import {
  BufferAttribute,
  ClampToEdgeWrapping,
  Color,
  DataTexture,
  InstancedBufferGeometry,
  LinearFilter,
  Mesh,
  OrthographicCamera,
  RedFormat,
  RGFormat,
  ShaderMaterial,
  Vector2,
  Vector4,
  WebGLRenderTarget,
  type Texture,
  type WebGLRenderer,
} from "three";
import type { AtmosphereUniforms } from "../atmosphere.ts";
import { GRASS_MASK_GLSL, type GrassMask } from "../grass-mask.ts";
import type { BlobCaster } from "../blob-shadows.ts";
import { GrassTrample, MAX_TRAMPLE } from "../grass-trample.ts";
import { assemble } from "./chunks.ts";

/** Densidade (R) e secura (G) do capim por bioma (doc 04, Biomas). */
const DENSITY: Record<number, [number, number]> = {
  [BIOME.mar]: [0, 0],
  [BIOME.praia]: [0.12, 0.6],
  [BIOME.mangue]: [0, 0],
  [BIOME.mata]: [1, 0.1],
  [BIOME.cerrado]: [0.75, 0.85],
  [BIOME.serra]: [0.25, 0.4],
};

/**
 * Densidade e secura da grama por célula do plano (8 m), pelo bioma. As pegadas dos POIs ficam
 * sem grama (doc 04), com uma célula de folga: a textura é interpolada e vazaria pela borda.
 */
export function createGrassDensityTexture(plan: Pick<PlanData, "biome"> & { pois?: PlanData["pois"] }): DataTexture {
  const data = new Uint8Array(PLAN_SIDE * PLAN_SIDE * 2);
  for (let i = 0; i < PLAN_SIDE * PLAN_SIDE; i++) {
    const [d, dry] = DENSITY[plan.biome[i] as number] ?? [0, 0];
    data[i * 2] = Math.round(d * 255);
    data[i * 2 + 1] = Math.round(dry * 255);
  }
  for (const poi of plan.pois ?? []) {
    const footprint = POI_RULE.get(poi.type)?.footprint ?? 0;
    if (footprint <= 0) continue;
    const r = footprint + PLAN_SPACING;
    const cell = (v: number) => Math.round((v + WORLD_HALF) / PLAN_SPACING);
    for (let j = Math.max(0, cell(poi.z - r)); j <= Math.min(PLAN_SIDE - 1, cell(poi.z + r)); j++) {
      for (let i = Math.max(0, cell(poi.x - r)); i <= Math.min(PLAN_SIDE - 1, cell(poi.x + r)); i++) {
        const dx = -WORLD_HALF + i * PLAN_SPACING - poi.x;
        const dz = -WORLD_HALF + j * PLAN_SPACING - poi.z;
        if (dx * dx + dz * dz <= r * r) data[(j * PLAN_SIDE + i) * 2] = 0;
      }
    }
  }
  const texture = new DataTexture(data, PLAN_SIDE, PLAN_SIDE, RGFormat);
  texture.minFilter = LinearFilter;
  texture.magFilter = LinearFilter;
  texture.wrapS = ClampToEdgeWrapping;
  texture.wrapT = ClampToEdgeWrapping;
  texture.needsUpdate = true;
  return texture;
}

/**
 * Duas janelas de 48² tufos de 7 cunhas (96.768 triângulos): a de perto, de células de 1,35 m (~65 m), e
 * a de longe, de células de 2,7 m (~130 m) e tufos mais abertos.
 */
const GRID = 48;
const CELL = 1.35;
const BLADES = 7;
const RING = GRID * GRID * BLADES;
/**
 * Lâminas por instância: 8 tufos (224 vértices). Uma lâmina por instância (4 vértices) dava 32 mil
 * instâncias minúsculas; o custo fixo de cada uma (no render em software, cada instância é um desenho)
 * dominava o quadro. `RING` é múltiplo: cada instância fica numa janela só.
 */
export const GRASS_BATCH = 8 * BLADES;

/**
 * Distâncias (m da câmera, pela raiz do tufo): entre `near` e `far` os tufos de perto dão lugar, um a
 * um, aos de longe (pontilhado); entre `fade` e `end` os de longe brotam do chão, cada um no seu trecho,
 * dentro da janela de longe. A borda fica perto do horizonte, onde um tufo tem poucos pixels.
 */
export const GRASS_REACH = { near: 24, far: 30, fade: 50, end: 60 } as const;

const f1 = (v: number) => (Number.isInteger(v) ? v.toFixed(1) : String(v));

const vertexShader = /* glsl */ `
${assemble("vertex", ["atmo", "wind", "blobVertex", "sunShadow"])}
uniform sampler2D uHeights;
uniform sampler2D uDensity;
uniform vec4 uOrigin;
uniform float uHeightSide;
uniform float uPlanSide;
uniform vec2 uWorldMin;
uniform float uWorldSize;
uniform float uDensidade;
${GRASS_MASK_GLSL}
uniform vec4 uTrample[${MAX_TRAMPLE}];
uniform float uTrampleCount;
in float aT;
in vec2 aBase;
// Qual das \`GRASS_BATCH\` lâminas da instância este vértice é.
in float aBlade;
out vec3 vWorldPos;
out float vT;
out float vDry;
out float vBlob;
out float vSun;
out float vFade;

float hash(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }

// Ruído de valor para as moitas (pesquisa 11): o capim junta em tufos irregulares com clareiras
// largas de chão nu, como nas referências, em vez de um tapete uniforme.
float clumpNoise(vec2 p) {
  vec2 i = floor(p);
  vec2 f = fract(p);
  vec2 u = f * f * (3.0 - 2.0 * f);
  return mix(mix(hash(i), hash(i + vec2(1.0, 0.0)), u.x), mix(hash(i + vec2(0.0, 1.0)), hash(i + vec2(1.0, 1.0)), u.x), u.y);
}

vec2 texel(vec2 xz, float side) {
  vec2 uv = clamp((xz - uWorldMin) / uWorldSize, 0.0, 1.0);
  return (uv * (side - 1.0) + 0.5) / side;
}

void main() {
  // A lâmina; janela de perto (0) ou de longe (1, células e leques maiores).
  float bladeIndex = float(gl_InstanceID) * ${GRASS_BATCH}.0 + aBlade;
  float ring = step(${RING}.0, bladeIndex);
  float scale = 1.0 + ring;
  float local = bladeIndex - ring * ${RING}.0;
  float id = floor(local / ${BLADES}.0);
  float blade = mod(local, ${BLADES}.0);
  vec2 cell = vec2(mod(id, ${GRID}.0), floor(id / ${GRID}.0));
  // O tufo e todas as suas cunhas ficam ancorados no mundo quando a janela acompanha a câmera.
  vec2 absCell = mix(uOrigin.xy, uOrigin.zw, ring) + cell;
  vec2 seed = absCell + ring * vec2(71.3, 19.9);
  vec2 root = (absCell + vec2(hash(seed), hash(seed + 17.3))) * ${CELL} * scale;
  // Cada tufo tem o seu trecho (hash da célula) de cada faixa, sem borda contínua à frente de quem anda.
  // Na troca de janela (o LOD, perto da câmera) ele se desfaz por pontilhado na tela, no tamanho inteiro;
  // na borda do alcance (só a janela de longe) ele brota do chão: a altura cresce no trecho dele.
  float distanceToEye = length(root - cameraPosition.xz);
  float handover = smoothstep(${f1(GRASS_REACH.near)}, ${f1(GRASS_REACH.far)}, distanceToEye);
  float cut = hash(seed + 11.7) * 0.6;
  float fade = smoothstep(cut, cut + 0.4, ring > 0.5 ? handover : 1.0 - handover);
  float edge = ring > 0.5 ? 1.0 - smoothstep(${f1(GRASS_REACH.fade)}, ${f1(GRASS_REACH.end)}, distanceToEye) : 1.0;
  float sprout = hash(seed + 23.9) * 0.6;
  float grow = smoothstep(sprout, sprout + 0.4, edge);
  grow = grow * (2.0 - grow);
  // Fora da sua faixa, o tufo sai antes das leituras de textura (metade das instâncias de cada janela).
  if (fade <= 0.0 || grow <= 0.0) {
    gl_Position = vec4(2.0, 2.0, 2.0, 1.0);
    return;
  }
  float angle = blade * 2.399963 + hash(seed + 3.3) * 6.2831;
  vec2 dir = vec2(cos(angle), sin(angle));
  vec2 across = vec2(dir.y, -dir.x);
  float spread = sqrt(blade / ${BLADES}.0) * (0.28 + 0.2 * hash(seed + 2.1));
  vec2 xz = root + dir * spread * mix(1.0, 1.8, ring);
  vec2 density = texture(uDensity, texel(xz, uPlanSide)).rg;
  float ground = texture(uHeights, texel(xz, uHeightSide)).r;
  // As moitas pela posição no mundo: as duas janelas desenham as mesmas.
  float clump = smoothstep(0.22, 0.68, clumpNoise(absCell * scale * 0.2));
  // Seleção por tufo: grupos densos pequenos separados por chão livre, não lâminas isoladas. Longe,
  // um tufo por 4 células de perto: mais tufos ficam.
  float keep = step(hash(seed + 5.1), min(1.0, density.r * uDensidade * clump * 0.72 * mix(1.0, 1.4, ring))) * step(0.15, ground);
  if (keep > 0.0 && grassBuilt(xz)) keep = 0.0;
  float variation = hash(seed + blade * 7.13 + 9.7);
  // Fase 14 (as duas referências): cristais de capim mais altos no meio do tufo e abertos em leque para fora.
  float height = (0.3 + 0.48 * variation) * (1.0 - 0.38 * spread) * keep * grow;
  float width = (0.07 + 0.045 * variation) * keep * mix(1.0, 1.6, ring) * mix(0.5, 1.0, grow);
  // Base triangular espessa, três faces e uma ponta: volume real até na vista de cima.
  vec2 base = (across * aBase.x + dir * aBase.y) * width;
  vec2 lean = dir * aT * height * 0.3;
  vec3 world = vec3(xz.x + base.x + lean.x, ground - 0.015 * keep + aT * height, xz.y + base.y + lean.y);
  world.xz += grassWind(xz) * aT * aT * height;
  // Pisoteio: só a ponta (as bases ficam no chão), só a janela de perto. Quem está no capim e as pegadas
  // recentes (\`GrassTrample\`) deitam a lâmina para fora, e ela volta conforme a pegada envelhece.
  if (aT > 0.5 && ring < 0.5 && keep > 0.0) {
    vec2 push = vec2(0.0);
    float press = 0.0;
    for (int i = 0; i < ${MAX_TRAMPLE}; i++) {
      if (float(i) >= uTrampleCount) break;
      vec4 t = uTrample[i];
      vec2 d = xz - t.xy;
      float d2 = dot(d, d);
      if (d2 >= t.z * t.z) continue;
      float k = (1.0 - smoothstep(t.z * 0.35, t.z, sqrt(d2))) * t.w;
      push += (d2 > 1e-4 ? d * inversesqrt(d2) : dir) * k;
      press = max(press, k);
    }
    float bend = min(length(push), 1.0);
    if (bend > 0.0) {
      world.xz += normalize(push) * bend * height * 0.75;
      world.y -= press * height * 0.55;
    }
  }
  vWorldPos = world;
  vT = aT;
  vDry = density.g;
  vFade = fade;
  // Sombras-blob (F51) pela base da lâmina, no vértice: o capim denso não esconde a sombra.
  vBlob = keep > 0.0 ? blobShadow(vec3(xz.x, ground, xz.y)) : 0.0;
  // Sombra do sol (Fase 13) por vértice, uma amostra: a sobreposição das lâminas não multiplica o custo.
  vSun = keep > 0.0 ? sunShadowFast(world, vec3(0.0, 1.0, 0.0)) : 1.0;
  gl_Position = projectionMatrix * viewMatrix * vec4(world, 1.0);
}
`;

const fragmentShader = /* glsl */ `
${assemble("fragment", ["atmo", "blobs", "fogGlow", "styleCore", "ringLight", "dither"])}
in vec3 vWorldPos;
in float vT;
in float vDry;
in float vBlob;
in float vSun;
in float vFade;

void main() {
  // Pesquisa 11: lâminas pálidas em cunha (base cinza-oliva → ponta quase branca; secas, um pouco
  // mais quentes), raiz escurecida — o capim claro contra o chão escuro é a leitura das referências.
  // Valores em linear (as direções de arte do plano estão em sRGB).
  vec3 base = mix(vec3(0.185, 0.198, 0.161), vec3(0.198, 0.193, 0.168), vDry);
  vec3 tip = mix(vec3(0.478, 0.485, 0.411), vec3(0.485, 0.472, 0.419), vDry);
  vec3 albedo = mix(base, tip, 0.22 + 0.78 * smoothstep(0.0, 0.82, vT));
  vec3 face = normalize(cross(dFdx(vWorldPos), dFdy(vWorldPos)));
  if (!gl_FrontFacing) face = -face;
  vec3 ambient = mix(uAmbientGround, uAmbientSky, clamp(face.y * 0.5 + 0.5, 0.0, 1.0));
  float lit = max(0.0, dot(face, uLightDir));
  float rootOcclusion = mix(0.62, 1.0, smoothstep(0.0, 0.45, vT));
  vec3 color = albedo * rootOcclusion * (ambient * 1.1 * (1.0 - uBlobStyle.w * vBlob) + uLightColor * lit * 0.8 * (1.0 - vBlob) * vSun);
  // O capim fica sem malha (densidade demais vira ruído) e quase neutro: metade da saturação que
  // sobra sai, e a cor da luz (quente/fria) atravessa, como no styleCompose.
  color = mix(vec3(dot(color, STYLE_LUMA)), color, 0.5);
  // O anel do jogador acende as lâminas em volta (Fase 13).
  color += ringLight(vWorldPos) * albedo * 2.2;
  color = applyFog(color, vWorldPos, cameraPosition);
  // Troca de janela e sumiço (vFade), depois de todas as derivadas.
  if (ditherThreshold() >= vFade) discard;
  gl_FragColor = vec4(color, 1.0);
  #include <tonemapping_fragment>
  #include <colorspace_fragment>
}
`;

export interface Grass {
  mesh: Mesh;
  follow(x: number, z: number): void;
  /**
   * Pisoteio do quadro: quem projeta sombra-blob (`BlobShadows.casters`) e está no chão deita o capim
   * em volta; as pegadas voltam em `TRAMPLE_RECOVER` s. `ground`: a altura do terreno; `time` em s.
   */
  trample(casters: readonly BlobCaster[], ground: (x: number, z: number) => number, time: number): void;
  /** Densidade das Opções (0 a 1): 0 esconde o capim (nenhuma draw call). */
  setDensity(k: number): void;
  /**
   * Só nos e2e (F53): pixels cobertos pelo capim num quadrado de `half` m em volta de (x, z), visto de
   * cima numa ortográfica de 128², com as lâminas paradas no tempo. `raw` desenha sem as construções.
   */
  probe(renderer: WebGLRenderer, x: number, z: number, half: number, raw?: boolean): number;
  /** Só nos e2e (F51): a mesma vista, com a cor média (0–255, linear) dos pixels cobertos pelo capim. */
  shade(renderer: WebGLRenderer, x: number, z: number, half: number): { covered: number; rgb: [number, number, number] };
  dispose(): void;
}

/** Lado (pixels) do alvo da sonda: uma lâmina vista de cima tem poucos mm² e some em pixels de 5 cm. */
const PROBE_SIZE = 128;
/** Tempo do vento na sonda: o mesmo a cada chamada, para as contagens baterem antes e depois. */
const PROBE_TIME = 1.7;

/**
 * Cunha triangular sem face inferior (o mesmo orçamento de três triângulos da lâmina antiga), repetida
 * `GRASS_BATCH` vezes por instância (`aBlade`).
 */
export function createGrassBladeGeometry(): InstancedBufferGeometry {
  const t = [0, 0, 0, 1];
  const base = [-1, -0.45, 1, -0.45, 0, 0.65, 0, 0];
  const faces = [0, 3, 1, 1, 3, 2, 2, 3, 0];
  const repeat = (values: number[], each: (v: number, blade: number) => number) => Array.from({ length: GRASS_BATCH }, (_, b) => values.map((v) => each(v, b))).flat();
  const geometry = new InstancedBufferGeometry();
  geometry.setAttribute("position", new BufferAttribute(new Float32Array(12 * GRASS_BATCH), 3));
  geometry.setAttribute("aT", new BufferAttribute(new Float32Array(repeat(t, (v) => v)), 1));
  geometry.setAttribute("aBase", new BufferAttribute(new Float32Array(repeat(base, (v) => v)), 2));
  geometry.setAttribute("aBlade", new BufferAttribute(new Float32Array(repeat(t, (_, b) => b)), 1));
  geometry.setIndex(repeat(faces, (v, b) => v + 4 * b));
  geometry.instanceCount = (2 * RING) / GRASS_BATCH;
  return geometry;
}

/**
 * Capim gerado na GPU (doc 04, Grama): uma única draw call; a posição de cada lâmina sai de
 * `gl_InstanceID` e `aBlade` (o lote da instância) numa grade ancorada no mundo, a altura da textura
 * do heightfield e a densidade de uma máscara por bioma. Sem atributos por instância. As construções do jogador o apagam pela
 * máscara de 2 m (`GrassMask`, F53).
 */
export function createGrass(atmosphere: AtmosphereUniforms, heights: Texture, plan: Pick<PlanData, "biome" | "pois">, mask: GrassMask["uniforms"]): Grass {
  const geometry = createGrassBladeGeometry();
  const density = createGrassDensityTexture(plan);
  const trample = new GrassTrample();
  const material = new ShaderMaterial({
    name: "capim",
    uniforms: {
      ...atmosphere,
      uHeights: { value: heights },
      uDensity: { value: density },
      uOrigin: { value: new Vector4() },
      uHeightSide: { value: HEIGHT_SIDE },
      uTrample: { value: trample.points },
      uTrampleCount: { value: 0 },
      uPlanSide: { value: PLAN_SIDE },
      uWorldMin: { value: new Vector2(-WORLD_HALF, -WORLD_HALF) },
      uWorldSize: { value: WORLD_SIZE },
      uDensidade: { value: 1 },
      ...mask,
    },
    vertexShader,
    fragmentShader,
  });
  material.side = 2; // DoubleSide: lâminas finas vistas dos dois lados
  const mesh = new Mesh(geometry, material);
  mesh.name = "capim";
  mesh.frustumCulled = false;
  const origin = material.uniforms["uOrigin"]!.value as Vector4;
  /** A primeira célula da janela de células de `cell` m centrada em `v`. */
  const corner = (v: number, cell: number) => Math.floor((v - (GRID * cell) / 2) / cell);
  const follow = (x: number, z: number) => origin.set(corner(x, CELL), corner(z, CELL), corner(x, 2 * CELL), corner(z, 2 * CELL));
  let probeKit: { target: WebGLRenderTarget; camera: OrthographicCamera; pixels: Uint8Array; empty: DataTexture } | undefined;
  /** Desenha só o capim, de cima, num quadrado de `half` m em volta de (x, z); devolve os pixels RGBA. */
  const draw = (renderer: WebGLRenderer, x: number, z: number, half: number, raw: boolean): Uint8Array => {
    if (!probeKit) {
      const empty = new DataTexture(new Uint8Array(1), 1, 1, RedFormat);
      empty.needsUpdate = true;
      probeKit = { target: new WebGLRenderTarget(PROBE_SIZE, PROBE_SIZE), camera: new OrthographicCamera(), pixels: new Uint8Array(PROBE_SIZE * PROBE_SIZE * 4), empty };
    }
    const { target, camera, pixels, empty } = probeKit;
    Object.assign(camera, { left: -half, right: half, top: half, bottom: -half, near: 1, far: 4000 });
    camera.up.set(0, 0, -1);
    camera.position.set(x, 2000, z);
    camera.lookAt(x, 0, z);
    camera.updateProjectionMatrix();
    camera.updateMatrixWorld();
    const u = material.uniforms;
    const saved = { origin: origin.clone(), trample: u["uTrampleCount"]!.value as number, time: u["uTime"]!.value as number, mask: u["uGrassMask"]!.value as Texture, count: u["uStationCount"]!.value as number };
    const clear = renderer.getClearColor(new Color());
    const alpha = renderer.getClearAlpha();
    const previous = renderer.getRenderTarget();
    follow(x, z);
    u["uTime"]!.value = PROBE_TIME;
    // Sem pisoteio: a sonda conta o capim de pé, com ou sem alguém em cima.
    u["uTrampleCount"]!.value = 0;
    // Sem as construções: uma máscara de 1 texel vazia (o shader confere o tamanho) e nenhuma estação.
    if (raw) {
      u["uGrassMask"]!.value = empty;
      u["uStationCount"]!.value = 0;
    }
    try {
      renderer.setClearColor(0x000000, 0);
      renderer.setRenderTarget(target);
      renderer.clear();
      renderer.render(mesh, camera);
      renderer.readRenderTargetPixels(target, 0, 0, PROBE_SIZE, PROBE_SIZE, pixels);
    } finally {
      renderer.setRenderTarget(previous);
      renderer.setClearColor(clear, alpha);
      origin.copy(saved.origin);
      u["uTime"]!.value = saved.time;
      u["uTrampleCount"]!.value = saved.trample;
      u["uGrassMask"]!.value = saved.mask;
      u["uStationCount"]!.value = saved.count;
    }
    return pixels;
  };
  return {
    mesh,
    follow,
    trample(casters, ground, time) {
      material.uniforms["uTrampleCount"]!.value = trample.update(casters, ground, time, origin.x * CELL + (GRID * CELL) / 2, origin.y * CELL + (GRID * CELL) / 2);
    },
    setDensity(k) {
      material.uniforms["uDensidade"]!.value = Math.max(0, Math.min(1, k));
      mesh.visible = k > 0;
    },
    probe(renderer, x, z, half, raw = false) {
      const pixels = draw(renderer, x, z, half, raw);
      let covered = 0;
      for (let i = 3; i < pixels.length; i += 4) if ((pixels[i] as number) > 0) covered++;
      return covered;
    },
    shade(renderer, x, z, half) {
      const pixels = draw(renderer, x, z, half, false);
      let [r, g, b, covered] = [0, 0, 0, 0];
      for (let i = 0; i < pixels.length; i += 4) {
        if ((pixels[i + 3] as number) === 0) continue;
        covered++;
        r += pixels[i] as number;
        g += pixels[i + 1] as number;
        b += pixels[i + 2] as number;
      }
      return { covered, rgb: covered ? [r / covered, g / covered, b / covered] : [0, 0, 0] };
    },
    dispose() {
      mesh.removeFromParent();
      geometry.dispose();
      density.dispose();
      material.dispose();
      probeKit?.target.dispose();
      probeKit?.empty.dispose();
    },
  };
}
